import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Smartphone, Monitor, Share, Plus, MoreVertical, Download } from 'lucide-react';

export const InstallPWAModal = ({ open, onOpenChange, deferredPrompt, setDeferredPrompt }) => {
  const { i18n } = useTranslation();
  const isPortuguese = i18n.language === 'pt-BR';
  
  const [deviceType, setDeviceType] = useState('desktop');
  
  useEffect(() => {
    const userAgent = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      setDeviceType('ios');
    } else if (/Android/.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }
  }, []);

  const handleNativeInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        onOpenChange(false);
      }
      setDeferredPrompt(null);
    }
  };

  const texts = {
    'pt-BR': {
      title: 'Instalar MagicStay Homes',
      subtitle: 'Instale o app para acesso rápido e experiência offline',
      installNow: 'Instalar Agora',
      close: 'Fechar',
      ios: {
        title: 'Como instalar no iPhone/iPad',
        steps: [
          { icon: Share, text: 'Toque no botão de Compartilhar na barra inferior do Safari' },
          { icon: Plus, text: 'Role para baixo e toque em "Adicionar à Tela de Início"' },
          { icon: Smartphone, text: 'Toque em "Adicionar" para confirmar' },
        ]
      },
      android: {
        title: 'Como instalar no Android',
        steps: [
          { icon: MoreVertical, text: 'Toque no menu (⋮) no canto superior direito' },
          { icon: Download, text: 'Toque em "Instalar aplicativo" ou "Adicionar à tela inicial"' },
          { icon: Smartphone, text: 'Toque em "Instalar" para confirmar' },
        ]
      },
      desktop: {
        title: 'Como instalar no Computador',
        steps: [
          { icon: Monitor, text: 'Use o navegador Chrome ou Edge' },
          { icon: Plus, text: 'Clique no ícone ⊕ na barra de endereços (à direita)' },
          { icon: Download, text: 'Clique em "Instalar" para confirmar' },
        ],
        altSteps: [
          { icon: MoreVertical, text: 'Ou clique no menu (⋮) no canto superior direito' },
          { icon: Download, text: 'Clique em "Instalar MagicStay Homes..."' },
        ]
      }
    },
    'en-US': {
      title: 'Install MagicStay Homes',
      subtitle: 'Install the app for quick access and offline experience',
      installNow: 'Install Now',
      close: 'Close',
      ios: {
        title: 'How to install on iPhone/iPad',
        steps: [
          { icon: Share, text: 'Tap the Share button in Safari\'s bottom bar' },
          { icon: Plus, text: 'Scroll down and tap "Add to Home Screen"' },
          { icon: Smartphone, text: 'Tap "Add" to confirm' },
        ]
      },
      android: {
        title: 'How to install on Android',
        steps: [
          { icon: MoreVertical, text: 'Tap the menu (⋮) in the top right corner' },
          { icon: Download, text: 'Tap "Install app" or "Add to Home screen"' },
          { icon: Smartphone, text: 'Tap "Install" to confirm' },
        ]
      },
      desktop: {
        title: 'How to install on Computer',
        steps: [
          { icon: Monitor, text: 'Use Chrome or Edge browser' },
          { icon: Plus, text: 'Click the ⊕ icon in the address bar (on the right)' },
          { icon: Download, text: 'Click "Install" to confirm' },
        ],
        altSteps: [
          { icon: MoreVertical, text: 'Or click the menu (⋮) in the top right corner' },
          { icon: Download, text: 'Click "Install MagicStay Homes..."' },
        ]
      }
    }
  };

  const t = texts[isPortuguese ? 'pt-BR' : 'en-US'];
  const deviceTexts = t[deviceType];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00C2D1] to-[#08172F] flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            {t.title}
          </DialogTitle>
          <DialogDescription>
            {t.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Native install button if available */}
          {deferredPrompt && (
            <Button 
              onClick={handleNativeInstall}
              className="w-full bg-[#00C2D1] hover:bg-[#00a8b5] text-white"
              size="lg"
            >
              <Download className="w-5 h-5 mr-2" />
              {t.installNow}
            </Button>
          )}

          {/* Instructions */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              {deviceType === 'ios' && <Smartphone className="w-5 h-5 text-gray-600" />}
              {deviceType === 'android' && <Smartphone className="w-5 h-5 text-green-600" />}
              {deviceType === 'desktop' && <Monitor className="w-5 h-5 text-blue-600" />}
              {deviceTexts.title}
            </h3>
            
            <ol className="space-y-3">
              {deviceTexts.steps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00C2D1]/10 flex items-center justify-center text-[#00C2D1] font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <step.icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{step.text}</span>
                  </div>
                </li>
              ))}
            </ol>

            {/* Alternative steps for desktop */}
            {deviceType === 'desktop' && deviceTexts.altSteps && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">{isPortuguese ? 'Alternativa:' : 'Alternative:'}</p>
                <ol className="space-y-2">
                  {deviceTexts.altSteps.map((step, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                      <step.icon className="w-4 h-4 text-gray-400" />
                      {step.text}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Device indicator */}
          <div className="flex justify-center gap-2 pt-2">
            <button
              onClick={() => setDeviceType('ios')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                deviceType === 'ios' 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              iPhone/iPad
            </button>
            <button
              onClick={() => setDeviceType('android')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                deviceType === 'android' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Android
            </button>
            <button
              onClick={() => setDeviceType('desktop')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                deviceType === 'desktop' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {isPortuguese ? 'Computador' : 'Computer'}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
